// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { reorder } from "@ces/App/Services/OrderService";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineCollectionModel, OrderModel } from "@insite/client-framework/Types/ApiModels";

type ReorderParameter = {
    orderNumber: string;
} & HasOnSuccess &
    HasOnError<string>;

export interface ReorderResult {
    successful?: boolean;
    errorMessage?: string;
    order?: OrderModel;
}

type HandlerType = ApiHandlerDiscreteParameter<ReorderParameter, CartLineCollectionModel, ReorderResult>;

export const DispatchBeginReorder: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
    props.dispatch({
        type: "Pages/OrderHistory/BeginReorder",
        orderNumber: props.parameter.orderNumber,
    });
};

export const XngReorder: HandlerType = async props => {
    props.apiResult = await reorder({ orderNumber: props.parameter.orderNumber });
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult?.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage as any);
    }
};
export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult?.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.();
    }
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart({ shouldLoadFullCart: true }));
};

export const DispatchCompleteReorder: HandlerType = props => {
    props.dispatch({
        type: "Pages/OrderHistory/CompleteReorder",
        orderNumber: props.parameter.orderNumber,
    });
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const chain = [
    DispatchBeginReorder,
    XngReorder,
    LoadCart,
    ExecuteOnErrorCallback,
    ExecuteOnSuccessCallback,
    DispatchCompleteReorder,
];

const xReorder = createHandlerChainRunner(chain, "xReorder");
export default xReorder;
